import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Coins, Download, Save, Trash2 } from 'lucide-react';
import { authAPI, subscriptionAPI } from '../utils/auth';
import { toast } from 'sonner';
import FileUploader from '../components/FileUploader/FileUploader';
import ModelViewer from '../components/viewer/ModelViewer';
import ModelImport from '../components/viewer/ModelImport';
import { paletteColor } from '../components/viewer/viewerUtils';
import { SiteLayout, useDocumentTitle } from '../components/platform/SiteLayout';
import { Container, Heading, Card, Btn } from '../components/platform/ui';
import TaskStatus from '../components/TaskStatus/TaskStatus';
import { medicalApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const currentLanguage = 'ru'

// Функция для определения типа кости по имени файла и порядку
const getBoneType = (filename, index, totalFiles, currentLanguage = 'ru') => {
  // Сначала пытаемся определить по имени файла
  const lowerName = filename.toLowerCase();
  
  if (currentLanguage === 'ru') {
    if (lowerName.includes('upper_jaw') || lowerName.includes('maxilla') || lowerName.includes('верхн')) {
      return 'Верхняя челюсть';
    }
    if (lowerName.includes('lower_jaw') || lowerName.includes('mandible') || lowerName.includes('нижн')) {
      return 'Нижняя челюсть';
    }
    if (lowerName.includes('upper_teeth') || lowerName.includes('верхние_зубы') || lowerName.includes('верх_зуб')) {
      return 'Верхние зубы';
    }
    if (lowerName.includes('lower_teeth') || lowerName.includes('нижние_зубы') || lowerName.includes('ниж_зуб')) {
      return 'Нижние зубы';
    }
    if (lowerName.includes('nerve') || lowerName.includes('нерв') || lowerName.includes('inferior_alveolar')) {
      return 'Нижнечелюстной нерв';
    }
    
    // Если по имени не определили, используем порядок (предполагаем стандартный порядок вывода)
    if (totalFiles === 5) {
      const orderMap = {
        0: 'Верхняя челюсть',
        1: 'Нижняя челюсть', 
        2: 'Верхние зубы',
        3: 'Нижние зубы',
        4: 'Нижнечелюстной нерв'
      };
      return orderMap[index] || `Сегмент ${index + 1}`;
    }
    
    // Для другого количества файлов
    return `Сегмент ${index + 1}`;
  } else {
    // English
    if (lowerName.includes('upper_jaw') || lowerName.includes('maxilla') || lowerName.includes('upper')) {
      return 'Upper Jaw';
    }
    if (lowerName.includes('lower_jaw') || lowerName.includes('mandible') || lowerName.includes('lower')) {
      return 'Lower Jaw';
    }
    if (lowerName.includes('upper_teeth') || lowerName.includes('upper_teeth') || lowerName.includes('upper_tooth')) {
      return 'Upper Teeth';
    }
    if (lowerName.includes('lower_teeth') || lowerName.includes('lower_teeth') || lowerName.includes('lower_tooth')) {
      return 'Lower Teeth';
    }
    if (lowerName.includes('nerve') || lowerName.includes('nerve') || lowerName.includes('inferior_alveolar')) {
      return 'Mandibular Nerve';
    }
    
    // Если по имени не определили, используем порядок
    if (totalFiles === 5) {
      const orderMap = {
        0: 'Upper Jaw',
        1: 'Lower Jaw', 
        2: 'Upper Teeth',
        3: 'Lower Teeth',
        4: 'Mandibular Nerve'
      };
      return orderMap[index] || `Segment ${index + 1}`;
    }
    
    // Для другого количества файлов
    return `Segment ${index + 1}`;
  }
};

// Функция для извлечения файлов из ZIP архива с правильными названиями
const extractFilesFromZip = async (zipBlob, currentLanguage = 'ru', ext = '.stl') => {
  const JSZip = await import('jszip');
  const zip = new JSZip.default();
  const zipContent = await zip.loadAsync(zipBlob);
  
  const stlFiles = [];
  
  // Извлекаем все STL файлы
  for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
    if (filename.toLowerCase().endsWith(ext) && !zipEntry.dir) {
      const blob = await zipEntry.async('blob');
      stlFiles.push({
        originalName: filename,
        blob: blob
      });
    }
  }
  
  // Сортируем файлы для consistency
  const sortedFiles = stlFiles.sort((a, b) => {
    const numA = a.originalName.match(/seg_?(\d+)/i) || a.originalName.match(/(\d+)/);
    const numB = b.originalName.match(/seg_?(\d+)/i) || b.originalName.match(/(\d+)/);
    
    if (numA && numB) {
      return parseInt(numA[1]) - parseInt(numB[1]);
    }
    
    return a.originalName.localeCompare(b.originalName);
  });
  
  // Присваиваем правильные названия
  return sortedFiles.map((file, index) => {
    const boneType = getBoneType(file.originalName, index, sortedFiles.length, currentLanguage);
    return {
      name: boneType,
      originalName: file.originalName,
      blob: file.blob
    };
  });
};

const generateRandomHexColor = () => {
  return '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
};

// Функция для генерации цветов по типу кости
const getColorForBoneType = (boneType) => {
  const colorMap = {
    'Верхняя челюсть': '#FF6B6B', // Красный
    'Нижняя челюсть': '#4ECDC4',  // Бирюзовый
    'Верхние зубы': '#FFD166',     // Желтый
    'Нижние зубы': '#06D6A0',      // Зеленый
    'Нижнечелюстной нерв': '#118AB2', // Синий
    'Upper Jaw': '#FF6B6B',
    'Lower Jaw': '#4ECDC4',
    'Upper Teeth': '#FFD166',
    'Lower Teeth': '#06D6A0',
    'Mandibular Nerve': '#118AB2'
  };
  return colorMap[boneType] || generateRandomHexColor();
};

const SegPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, logout } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [tokens, setTokens] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [currentTask, setCurrentTask] = useState(null);
  const [models, setModels] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [serverStatus, setServerStatus] = useState('checking');
  const [taskCompleted, setTaskCompleted] = useState(false);
  const [isSavingToHistory, setIsSavingToHistory] = useState(false);
  const [currentDicomZipId, setCurrentDicomZipId] = useState(null);
  
  const { t, i18n } = useTranslation();
  useDocumentTitle(t('segmentation.title'));

  // Detect current language from URL and update i18n
  useEffect(() => {
    const lang = location.pathname.includes('/en') ? 'en' : 'ru';
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [location.pathname, i18n]);

  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      toast.error(i18n.language === 'ru' ? t('segmentation.accessRequired') : t('segmentation.accessRequired'));
      navigate(i18n.language === 'ru' ? '/login' : '/login/en');
      return;
    }
    loadUserData();
    checkServerStatus();
  }, [navigate, i18n]);

  const loadUserData = async () => {
    try {
      if (authUser) {
        setTokens(authUser.tokens || 0);
        setIsAdmin(authUser.is_admin || false);
      } else {
        const userData = await authAPI.getProfile();
        setTokens(userData.tokens || 0);
        setIsAdmin(userData.is_admin || false);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error(i18n.language === 'ru' ? t('segmentation.userDataError') : t('segmentation.userDataError'));
      logout();
      navigate(i18n.language === 'ru' ? '/login' : '/login/en');
    }
  };

  const checkServerStatus = async () => {
    try {
      const isHealthy = await medicalApi.healthCheck();
      setServerStatus(isHealthy ? 'online' : 'offline');
    } catch (error) {
      setServerStatus('offline');
      console.error('Сервер недоступен:', error);
    }
  };

  const handleFilesSelected = (files, fileType) => {
    console.log('Выбраны файлы:', files.length, 'тип:', fileType);
    setError(null);
    setSelectedFile(files[0]);
  };

  const handleStlUpload = (stlFile) => {
    console.log('Загружен STL файл:', stlFile.name);
    setError(null);
    setCurrentTask(null);
    setTaskCompleted(false);
    
    const url = URL.createObjectURL(stlFile);
    
    const newModel = {
      id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      url: url,
      isCustom: true,
      name: stlFile.name,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      format: 'stl',
      color: generateRandomHexColor(),
      opacity: 0.95,
      visible: true,
      wireframe: false
    };
    
    setModels(prevModels => [...prevModels, newModel]);
  };

  // Свои модели в любых поддерживаемых форматах: остаются в браузере, на сервер не отправляются
  const handleImportModels = (imported) => {
    setError(null);
    setModels(prevModels => [
      ...prevModels,
      ...imported.map((m, i) => ({
        id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        url: URL.createObjectURL(m.blob),
        format: m.format,
        isCustom: true,
        name: m.name,
        color: paletteColor(prevModels.length + i),
        opacity: 1,
      })),
    ]);
  };

  const handleProcess = async (files, fileType, threshold) => {
    if (!isAdmin && tokens <= 0) {
      toast.error(i18n.language === 'ru' ? t('segmentation.insufficientTokens') : t('segmentation.insufficientTokens'));
      navigate(i18n.language === 'ru' ? '/subscription' : '/subscription/en');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setCurrentTask(null);

    try {
      console.log('🚀 Запуск обработки...');
      let response;
      
      if (fileType === 'nii') {
        response = await medicalApi.uploadNiiFile(files[0], threshold);
      } else if (fileType === 'mha') {
        response = await medicalApi.uploadMhaFile(files[0], threshold);
      } else if (fileType === 'dicom_zip') {
        response = await medicalApi.uploadDicomZip(files[0]);
      } else if (fileType === 'dicom') {
        response = await medicalApi.uploadDicomFolder(files);
      } else {
        response = await medicalApi.uploadDicomFolder(files);
      }

      console.log('✅ Ответ сервера:', response);
      setCurrentTask(response.task_id);
      refreshBalance();
      
    } catch (error) {
      console.error('❌ Ошибка обработки:', error);
      if (error.response?.status === 402) {
        toast.error(i18n.language === 'ru' ? t('segmentation.redirectMessage') : t('segmentation.redirectMessage'));
        setTimeout(() => navigate(i18n.language === 'ru' ? '/subscription' : '/subscription/en'), 2000);
      } else {
        const errorMsg = i18n.language === 'ru'
          ? error.response?.data?.detail || t('segmentation.sendFileError')
          : error.response?.data?.detail || t('segmentation.sendFileError');
        setError(errorMsg);
        toast.error(errorMsg);
      }
      setIsProcessing(false);
    }
  };

  // Сегментация списывается на сервере в момент приёма файла (и возвращается при ошибке/отмене).
  // Клиент только обновляет отображаемый остаток.
  const refreshBalance = async () => {
    try {
      const info = await subscriptionAPI.getTokens();
      setTokens(info.tokens);
    } catch (error) {
      console.error('Не удалось обновить остаток сегментаций:', error);
    }
  };

  const handleTaskComplete = async (statusData) => {
    console.log('✅ Задача завершена, получаем результаты');
    setTaskCompleted(true);
    setIsProcessing(false);
  };

  const loadTaskResults = async () => {
    if (!currentTask || !taskCompleted) return;

    try {
      console.log('📦 Загрузка результатов задачи:', currentTask);
      setIsDownloading(true);
      
      const resultsInfo = await medicalApi.getResultsInfo(currentTask);
      console.log('📋 Информация о результатах:', resultsInfo);
      
      // Для просмотра берём облегчённые VTK-модели (меньше файл, меньше граней — просмотрщик не тормозит).
      // Если сервер их не создал (старая версия сервиса), скачиваем обычный STL.
      let stlBlob = null;
      let format = 'stl';
      try {
        const vtkBlob = await medicalApi.downloadFile(currentTask, 'vtk');
        if (vtkBlob.type === 'application/zip') {
          const vtkFiles = await extractFilesFromZip(vtkBlob, currentLanguage, '.vtk');
          if (vtkFiles.length > 0) {
            stlBlob = vtkBlob;
            format = 'vtk';
          }
        }
      } catch (vtkError) {
        console.warn('VTK-модели недоступны, используем STL:', vtkError.message);
      }
      if (!stlBlob) {
        stlBlob = await medicalApi.downloadFile(currentTask, 'stl');
      }
      
      let newModels = [];
      
      if (stlBlob.type === 'application/zip') {
        console.log('📥 Получен ZIP архив, извлекаем файлы...');
        
        const stlFiles = await extractFilesFromZip(stlBlob, currentLanguage, format === 'vtk' ? '.vtk' : '.stl');
        
        newModels = await Promise.all(stlFiles.map(async (file, index) => {
          const url = URL.createObjectURL(file.blob);
          const position = [0, 0, 0];
          
          return {
            id: `processed-${currentTask}-${index}`,
            url: url,
            format: format,
            isCustom: false,
            name: file.name,
            originalName: file.originalName,
            position: position,
            rotation: [0, 0, 0],
            scale: [1, 1, 1],
            color: getColorForBoneType(file.name),
            opacity: 0.95,
            visible: true,
            wireframe: false
          };
        }));
        
      } else {
        const url = URL.createObjectURL(stlBlob);
        newModels = [{
          id: `processed-${currentTask}`,
          url: url,
          isCustom: false,
          name: currentLanguage === 'ru' ? `Сегментация ${new Date().toLocaleDateString()}` : `Segmentation ${new Date().toLocaleDateString()}`,
          position: [0, 0, 0],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          color: "#3498db",
          opacity: 0.95,
          visible: true,
          wireframe: false
        }];
      }
      
      setModels(prevModels => [...prevModels, ...newModels]);
      setIsDownloading(false);
      setTaskCompleted(false);

      // СПИСЫВАЕМ ТОКЕНЫ ПОСЛЕ УСПЕШНОЙ ЗАГРУЗКИ STL
      await refreshBalance();

      toast.success(i18n.language === 'ru' ? t('segmentation.completedMessage') : t('segmentation.completedMessage'));

    } catch (error) {
      console.error('❌ Ошибка при загрузке результата:', error);
      const errorMsg = i18n.language === 'ru'
        ? t('segmentation.resultLoadError') + ': ' + (error.message || t('segmentation.unknownError'))
        : t('segmentation.resultLoadError') + ': ' + (error.message || t('segmentation.unknownError'));
      setError(errorMsg);
      toast.error(errorMsg);
      setIsDownloading(false);
      setTaskCompleted(false);
    }
  };

  useEffect(() => {
    if (taskCompleted) {
      loadTaskResults();
    }
  }, [taskCompleted]);

  const handleDownloadStl = async (modelId = null) => {
    if (!currentTask && !modelId) return;

    setIsDownloading(true);
    try {
      let stlBlob;
      
      if (modelId) {
        const model = models.find(m => m.id === modelId);
        if (model) {
          const response = await fetch(model.url);
          stlBlob = await response.blob();
        }
      } else {
        stlBlob = await medicalApi.downloadFile(currentTask, 'stl');
      }
      
      const url = URL.createObjectURL(stlBlob);
      
      const model = modelId ? models.find(m => m.id === modelId) : null;
      const filename = model ? `${model.name}.${model.format || 'stl'}` : 'medical_segmentation.stl';
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(i18n.language === 'ru' ? t('segmentation.downloadSuccess') : t('segmentation.downloadSuccess'));

    } catch (error) {
      console.error('❌ Ошибка скачивания:', error);
      setError(i18n.language === 'ru' ? t('segmentation.downloadError') : t('segmentation.downloadError'));
      toast.error(i18n.language === 'ru' ? t('segmentation.downloadError') : t('segmentation.downloadError'));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRemoveModel = (modelId) => {
    setModels(prevModels => {
      const modelToRemove = prevModels.find(m => m.id === modelId);
      if (modelToRemove && modelToRemove.url.startsWith('blob:')) {
        URL.revokeObjectURL(modelToRemove.url);
      }
      return prevModels.filter(model => model.id !== modelId);
    });
  };

  const handleSaveToHistory = async (modelId = null) => {
    if (models.length === 0) {
      toast.error(i18n.language === 'ru' ? t('segmentation.noModelToSave') : t('segmentation.noModelToSave'));
      return;
    }

    setIsSavingToHistory(true);
    try {
      // Определяем, какие модели сохранять
      const modelsToSave = modelId
        ? models.filter(m => m.id === modelId)
        : models.filter(m => !m.isCustom); // Сохраняем только обработанные модели

      if (modelsToSave.length === 0) {
        throw new Error('No models to save');
      }

      // Generate a unique ID for this scan session
      const scanId = `scan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      let savedCount = 0;

      // Сохраняем каждую модель
      for (const model of modelsToSave) {
        try {
          // Get the blob from the model URL
          const response = await fetch(model.url);
          const stlBlob = await response.blob();

          const fileName = `${model.name || 'segmentation'}.stl`;

          // Create FormData for file upload
          const formData = new FormData();
          formData.append('file', stlBlob, fileName);
          formData.append('dicom_zip_id', scanId);
          formData.append('file_name', fileName);

          // Save to history with file upload
          await medicalApi.saveToHistory(formData);
          savedCount++;

          console.log(`✅ Saved model ${savedCount}/${modelsToSave.length}: ${fileName}`);
        } catch (error) {
          console.error(`❌ Error saving model ${model.name}:`, error);
        }
      }

      if (savedCount > 0) {
        toast.success(
          i18n.language === 'ru'
            ? `Сохранено ${savedCount} из ${modelsToSave.length} моделей`
            : `Saved ${savedCount} of ${modelsToSave.length} models`
        );
      } else {
        toast.error(i18n.language === 'ru' ? t('segmentation.saveError') : t('segmentation.saveError'));
      }
    } catch (error) {
      console.error('❌ Error saving to history:', error);
      toast.error(i18n.language === 'ru' ? t('segmentation.saveError') : t('segmentation.saveError'));
    } finally {
      setIsSavingToHistory(false);
    }
  };

  const handleClearAllModels = () => {
    models.forEach(model => {
      if (model.url.startsWith('blob:')) {
        URL.revokeObjectURL(model.url);
      }
    });
    setModels([]);
    setTaskCompleted(false);
    setCurrentTask(null);
  };

  // Модели для просмотрщика: у каждой своя группа, чтобы прозрачность и цвет настраивались отдельно
  const viewerItems = useMemo(() => models.map((m) => ({
    id: m.id, name: m.name, group: m.id, color: m.color,
    load: async () => ({ buffer: await (await fetch(m.url)).arrayBuffer(), format: m.format || 'stl' }),
  })), [models]);
  const viewerGroups = useMemo(() => Object.fromEntries(
    models.map((m) => [m.id, { label: m.name, opacity: m.opacity ?? 1 }]),
  ), [models]);

  const actionBtn = 'p-2 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors disabled:opacity-40';
  const modelActions = (group, item) => {
    const model = models.find((m) => m.id === item.id);
    if (!model) return null;
    return (
      <div className="flex gap-1">
        {!model.isCustom && (
          <button type="button" className={actionBtn} onClick={() => handleSaveToHistory(model.id)}
                  disabled={isSavingToHistory} title={t('segmentation.save')}><Save size={16} /></button>
        )}
        <button type="button" className={actionBtn} onClick={() => handleDownloadStl(model.id)}
                disabled={isDownloading} title={t('segmentation.download')}><Download size={16} /></button>
        <button type="button" className={actionBtn} onClick={() => handleRemoveModel(model.id)}
                title={t('segmentation.delete')}><Trash2 size={16} /></button>
      </div>
    );
  };
  const subscriptionPath = i18n.language === 'ru' ? '/subscription' : '/subscription/en';

  return (
    <SiteLayout>
      <Container className="py-10 max-w-7xl">
        <div data-testid="segmentation-page">
          <Heading level={1} className="text-4xl sm:text-5xl mb-3">{t('segmentation.title')}</Heading>
          <p className="text-lg text-gray-700 max-w-4xl mb-5" data-testid="page-subtitle">{t('segmentation.subtitle')}</p>

          {/* Статус сервера, остаток сегментаций, техобслуживание */}
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <span className={`inline-block px-4 py-2 text-white ${serverStatus === 'online' ? 'bg-green-600' : 'bg-[#C41E3A]'}`}>
              {serverStatus === 'online' ? t('segmentation.serverOnline') : t('segmentation.serverOffline')}
            </span>
            <Link to={subscriptionPath} data-testid="token-balance-link"
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors">
              <Coins size={20} className="text-[#C41E3A]" />
              <span className="font-bold">{isAdmin ? '∞' : tokens} {t('segmentation.tokens')}</span>
            </Link>
          </div>

          {!isAdmin && tokens <= 3 && (
            <div className="mb-8 px-4 py-3 bg-[#C41E3A] text-white" data-testid="low-tokens-warning">
              {`${t('segmentation.lowTokensWarning')} ${tokens} ${t('segmentation.tokens')}. `}
              <Link to={subscriptionPath} className="underline font-bold">{t('segmentation.topUpBalance')}</Link>
            </div>
          )}

          {/* Загрузка снимка */}
          <Card className="mb-8">
            <FileUploader
              onFilesSelected={handleFilesSelected}
              onProcess={handleProcess}
              onStlUpload={handleStlUpload}
              isLoading={isProcessing}
              currentLanguage={currentLanguage}
            />

            {error && (
              <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700">
                <h3 className="font-bold">{t('segmentation.error')}</h3>
                <p>{error}</p>
                <button onClick={() => setError(null)} className="mt-2 px-4 py-2 bg-red-600 text-white hover:bg-red-700">
                  {t('segmentation.close')}
                </button>
              </div>
            )}

            {serverStatus === 'offline' && (
              <div className="mt-4 p-4 bg-yellow-100 border border-yellow-400 text-yellow-800">
                <h3 className="font-bold">{t('segmentation.warning')}</h3>
                <p>{t('segmentation.serverUnavailable')}</p>
                <button onClick={checkServerStatus} className="mt-2 px-4 py-2 bg-yellow-600 text-white hover:bg-yellow-700">
                  {t('segmentation.checkAgain')}
                </button>
              </div>
            )}

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200">
              <h4 className="font-bold text-blue-800 mb-1">{t('segmentation.serverHours')}</h4>
              <p className="text-blue-700 text-sm">{t('segmentation.serverHoursText')}</p>
            </div>
          </Card>

          {/* Ход обработки */}
          {currentTask && (
            <Card className="mb-8">
              <TaskStatus
                taskId={currentTask}
                onTaskComplete={handleTaskComplete}
                onTaskFailed={() => { setIsProcessing(false); refreshBalance(); }}
                currentLanguage={currentLanguage}
              />
            </Card>
          )}

          {/* Свои модели и просмотр */}
          <Card className="mb-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <Heading level={2} className="text-2xl">
                {t('pl.medai.viewerTitle')}{models.length > 0 ? ` (${models.length})` : ''}
              </Heading>
              {models.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {models.some((m) => !m.isCustom) && (
                    <Btn variant="secondary" onClick={() => handleSaveToHistory()} disabled={isSavingToHistory}>
                      <Save size={18} />{t('pl.medai.saveAll')}
                    </Btn>
                  )}
                  <Btn onClick={handleClearAllModels}>{t('segmentation.clearAll')}</Btn>
                </div>
              )}
            </div>
            <div className="mb-5"><ModelImport onModels={handleImportModels} /></div>
            {isDownloading && <p className="text-base text-gray-600 mb-3 animate-pulse">{t('pl.medai.downloading')}</p>}
            {models.length > 0
              ? <ModelViewer items={viewerItems} groups={viewerGroups} orientation="none" groupActions={modelActions} />
              : <p className="text-base text-gray-600">{t('pl.medai.viewerEmpty')}</p>}
          </Card>

          {/* Как это работает */}
          <div className="mt-12 pt-8 border-t-2 border-black">
            <Heading level={2} className="text-2xl mb-4">{t('segmentation.howItWorks')}</Heading>
            <ol className="space-y-3 text-gray-700 text-lg" data-testid="info-list">
              {[
                <span key="1">{t('segmentation.step1')}</span>,
                <span key="2">{t('segmentation.step2')}</span>,
                <span key="3"><strong>{t('segmentation.step3Strong')}</strong>{t('segmentation.step3Text')}</span>,
                <span key="4">{t('segmentation.step4')}</span>,
              ].map((text, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#C41E3A] text-white flex items-center justify-center font-bold">{i + 1}</span>
                  {text}
                </li>
              ))}
            </ol>

            <div className="mt-8">
              <h3 className="text-xl font-bold mb-3">{t('segmentation.structuresTitle')}</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-700">
                {['upperJaw', 'lowerJaw', 'upperTeeth', 'lowerTeeth', 'mandibularNerve'].map((k) => (
                  <li key={k}>• {t(`segmentation.${k}`)}</li>
                ))}
              </ul>
            </div>

            <div className="mt-8 p-4 bg-green-50 border border-green-200">
              <h3 className="font-bold text-green-800 mb-2">{t('segmentation.tokenCharging')}</h3>
              <p className="text-green-700">
                <strong>{t('segmentation.tokenChargingStrong')}</strong><br />
                {t('segmentation.tokenChargingText')}
              </p>
            </div>
          </div>
        </div>
      </Container>
    </SiteLayout>
  );
};

export default SegPage;
